import { ArrowLeft, Bomb, ExternalLink, KeyRound, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useClient, useDeleteClient, usePollClient, useRotateSecret } from "@/api/hooks";
import { ClientStatusBadge } from "@/components/ClientStatusBadge";
import { Layout } from "@/components/Layout";
import { SnapshotStatusBadge } from "@/components/SnapshotStatusBadge";
import { TeardownDialog } from "@/components/TeardownDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function ClientDetailPage() {
	const { id = "" } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const clientQuery = useClient(id);
	const poll = usePollClient(id);
	const rotate = useRotateSecret(id);
	const del = useDeleteClient();
	const [newSecret, setNewSecret] = useState<string | null>(null);
	const [teardownOpen, setTeardownOpen] = useState(false);

	async function onRotate() {
		if (!window.confirm("Rotate shared secret? The old secret stops working immediately.")) return;
		const result = await rotate.mutateAsync();
		setNewSecret(result.shared_secret);
	}

	async function onDelete() {
		if (!clientQuery.data) return;
		const slug = clientQuery.data.slug;
		const confirmed = window.prompt(`Type "${slug}" to confirm deletion:`);
		if (confirmed !== slug) return;
		await del.mutateAsync(id);
		navigate("/", { replace: true });
	}

	if (clientQuery.isLoading) {
		return (
			<Layout>
				<p className="text-sm text-muted-foreground">Loading client…</p>
			</Layout>
		);
	}

	if (clientQuery.error) {
		return (
			<Layout>
				<p className="text-sm text-destructive">
					{clientQuery.error instanceof ApiError ? clientQuery.error.detail : "Load failed."}
				</p>
			</Layout>
		);
	}

	const client = clientQuery.data;
	if (!client) return null;

	return (
		<Layout>
			<div className="mx-auto max-w-4xl space-y-6">
				<Link
					to="/"
					className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
				>
					<ArrowLeft className="h-3 w-3" aria-hidden />
					All clients
				</Link>

				<header className="flex flex-wrap items-start justify-between gap-4">
					<div className="space-y-1">
						<h2 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
							{client.display_name}
							<ClientStatusBadge status={client.status} />
						</h2>
						<p className="text-sm text-muted-foreground">
							<span className="font-mono">{client.slug}</span> ·{" "}
							<a
								href={client.portal_url}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 hover:underline"
							>
								{client.portal_url}
								<ExternalLink className="h-3 w-3" aria-hidden />
							</a>
						</p>
					</div>
					<div className="flex items-center gap-2">
						<Button
							size="sm"
							variant="outline"
							onClick={() => poll.mutate()}
							disabled={poll.isPending}
						>
							{poll.isPending ? "Polling…" : "Poll now"}
						</Button>
						<Button size="sm" variant="outline" onClick={onRotate} disabled={rotate.isPending}>
							<KeyRound className="mr-1 h-3 w-3" aria-hidden />
							{rotate.isPending ? "Rotating…" : "Rotate secret"}
						</Button>
						<Button size="sm" variant="outline" onClick={onDelete} disabled={del.isPending}>
							<Trash2 className="mr-1 h-3 w-3" aria-hidden />
							Delete row only
						</Button>
						<Button
							size="sm"
							variant="destructive"
							onClick={() => setTeardownOpen(true)}
							title="Destroy Fly app + Postgres + DNS record + aggregator row"
						>
							<Bomb className="mr-1 h-3 w-3" aria-hidden />
							Tear down
						</Button>
					</div>
				</header>

				<TeardownDialog
					clientId={id}
					clientSlug={client.slug}
					clientDisplayName={client.display_name}
					open={teardownOpen}
					onOpenChange={setTeardownOpen}
					onComplete={() => {
						// Navigate back to dashboard after the admin clicks Done.
						// (The Dialog closes itself; this fires before close.)
						setTimeout(() => navigate("/", { replace: true }), 0);
					}}
				/>

				{newSecret ? (
					<Card>
						<CardHeader>
							<CardTitle>New shared secret</CardTitle>
							<CardDescription>
								Copy now — only shown once. Paste into the client portal's AGGREGATOR_SHARED_SECRET
								env var, then redeploy or restart the portal.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
								{newSecret}
							</pre>
							<div className="mt-2 flex justify-end gap-2">
								<Button variant="outline" onClick={() => navigator.clipboard.writeText(newSecret)}>
									Copy
								</Button>
								<Button onClick={() => setNewSecret(null)}>Dismiss</Button>
							</div>
						</CardContent>
					</Card>
				) : null}

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Current health</CardTitle>
					</CardHeader>
					<CardContent>
						<SnapshotStatusBadge
							snapshot={client.latest_snapshot}
							onPoll={() => poll.mutate()}
							isPolling={poll.isPending}
						/>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Recent snapshots</CardTitle>
						<CardDescription>Last 50 health polls, newest first.</CardDescription>
					</CardHeader>
					<CardContent>
						{client.snapshots.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								No polls yet — click "Poll now" to record the first snapshot.
							</p>
						) : (
							<ul className="space-y-2 text-sm">
								{client.snapshots.map((s) => (
									<li
										key={s.id}
										className="flex flex-wrap items-center justify-between gap-2 border-b pb-2 last:border-0"
									>
										<SnapshotStatusBadge snapshot={s} />
										<span className="font-mono text-xs text-muted-foreground">
											{new Date(s.polled_at).toLocaleString()}
										</span>
									</li>
								))}
							</ul>
						)}
					</CardContent>
				</Card>
			</div>
		</Layout>
	);
}
